import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { VariantDisplayTypeValues } from "@insite/client-framework/Types/ApiModels";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
// import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers"; Store Custom Code
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
// import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer"; Store Custom Code
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// Valtech Custom Code ->
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ConfigSectionModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@store/Overrides/Components/ProductPartNumbers";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import { useAnalytics } from "@store/utilities/hooks";
import ProductFitType, { ProductFitTypeStyles, wrapperFit } from "@store/Widgets/Components/ProductFitType";
import ProductMiniPhoto, { ProductMiniPhotoStyles } from "@store/Widgets/ProductList/ProductMiniPhotoStore";
import RsswProductModel, { RsswProductModelStyles } from "@store/Widgets/ProductList/RsswProductModel";
import Color from "color";
// <- Valtech Custom Code

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
    // Store Custom Code
    showMiniPhoto: boolean;
    showEta: boolean;
    showIndustryNumber: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    container?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    swatchGridItem?: GridItemProps;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    attributesGridItem?: GridItemProps;
    attributes?: ProductAttributesStyles;
    searchScoreDataGridItem?: GridItemProps;
    // Store Custom Code
    wrapperConfigs?: InjectableCss;
    configSectionWrapper?: InjectableCss;
    configSectionLabel?: TypographyPresentationProps;
    sectionOptionText?: TypographyPresentationProps;
    availabilityWrapper?: InjectableCss;
    miniPhotoWrapper?: InjectableCss;
    miniPhoto?: ProductMiniPhotoStyles;
    productModel?: RsswProductModelStyles;
    productFitType?: ProductFitTypeStyles;
    etaTitle?: TypographyPresentationProps;
    etaText?: TypographyPresentationProps;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    /* Store Custom Code - Start */
    container: {
        gap: 0,
        css: css`
            width: 100%;

            &.cart-hidden {
                flex-grow: 0;
            }

            ${({ theme }) => {
                return breakpointValuesMediaQueries(
                    theme,
                    [{ values: [null, null, `5px`, null, null], properties: ["padding-bottom"] }],
                    mediaOptions.min,
                );
            }}

            ${GridOffset} {
                width: 100% !important;
            }
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
                margin-top: 5px;
                margin-bottom: 0;
            `,
            typographyProps: {
                variant: "h3",
                css: css`
                    color: ${COLORS.secondary.base} !important;
                    font-size: 16px;
                    font-weight: ${TYPOGRAPHY.weight.black};
                    text-transform: uppercase;
                `,
            },
        },
    },
    wrapperConfigs: {
        css: css`
            margin-top: 10px;
        `,
    },
    configSectionWrapper: {
        css: css`
            margin-bottom: 5px;
        `,
    },
    configSectionLabel: {
        weight: TYPOGRAPHY.weight.bold,
        color: COLORS.text.dark,
        css: css`
            display: block;
        `,
    },
    sectionOptionText: {
        css: css`
            display: block;
        `,
    },
    availabilityWrapper: {
        css: css`
            &.cart-hidden {
                flex-grow: 1;
            }
        `,
    },
    availabilityGridItem: {
        width: 12,
        css: css`
            padding-left: 0 !important;
            padding-bottom: 0 !important;
        `,
    },
    partNumbersStyles: {
        container: {
            gap: 0,
            css: css`
                margin-bottom: 0;
            `,
        },
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            align-items: center;
            min-height: 60px;
        `,
    },
    miniPhotoWrapper: {
        css: css`
            margin-left: auto;
        `,
    },
    miniPhoto: {
        wrapper: {
            css: css`
                margin: 0;
            `,
        },
        imageWrapper: {
            css: css`
                overflow: hidden;
                position: relative;
                margin-right: 0;

                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [
                            { values: ["60px", null, null, null, null], properties: ["width"] },
                            { values: ["60px", null, null, null, null], properties: ["height"] },
                        ],
                        mediaOptions.min,
                    )}
            `,
        },
        miniPhotoLink: {
            css: css`
                display: block;
            `,
        },
        thumbOverlay: {
            css: css`
                position: absolute;
                width: 100%;
                height: 100%;
                background-color: ${Color(COLORS.secondary.base).rgb().alpha(0.4).string()};
                z-index: 1;
                display: flex;
                align-items: center;
                justify-content: center;
            `,
        },
        thumbOverlayText: {
            size: TYPOGRAPHY.size.smaller,
        },
    },
    productModel: {
        modelName: {
            size: TYPOGRAPHY.size.larger,
            css: css`
                margin-bottom: 0;
                margin-right: auto;
            `,
        },
    },
    productFitType: {
        wrapperDirectFit: {
            css: css`
                ${wrapperFit}
                flex-grow: 0;
            `,
        },
        wrapperMultiFit: {
            css: css`
                ${wrapperFit}
                flex-grow: 0;
            `,
        },
    },
    brandGridItem: {
        width: 12,
        css: css`
            padding-bottom: 8px;
            align-items: center;
            min-height: 38px;
        `,
    },
    etaTitle: {
        weight: TYPOGRAPHY.weight.bold,
        color: COLORS.text.dark,
        size: TYPOGRAPHY.size.small,
    },
    etaText: {
        color: COLORS.text.dark,
        size: TYPOGRAPHY.size.small,
        css: css`
            margin-left: 5px;
        `,
    },

    partNumbersGridItem: {
        width: 12,
    },
    attributesGridItem: {
        width: 12,
    },
    /* Store Custom Code - End */
};

const styles = productInformationStyles;

const ProductListProductInformation = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
    // Store Custom Code
    showMiniPhoto,
    showEta,
    showIndustryNumber,
}: Props) => {
    const { product } = productContext;
    /* Store Custom Code - Start */
    const { dispatchProductClickEvent } = useAnalytics();

    if (!product) {
        return null;
    }

    const handleAnalyticsClick = () => {
        dispatchProductClickEvent(productContext);
    };

    const configSections: any = product.detail?.configuration?.configSections;
    const canadianTireNumber = product?.properties?.canadianTireInfo
        ? JSON.parse(product.properties.canadianTireInfo).canadianTireNumber
        : null;

    const hasProductModel = product.detail?.modelNumber !== "";
    const productIndustryNumber = showIndustryNumber ? product.properties?.industryNumber : undefined;

    return (
        <>
            <GridContainer {...styles.container}>
                {product.brand && showBrand && (
                    <GridItem {...styles.brandGridItem}>
                        <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                    </GridItem>
                )}
                <GridItem {...styles.brandGridItem}>
                    {hasProductModel && (
                        <RsswProductModel product={productContext} extendedStyles={styles.productModel} />
                    )}
                    <ProductFitType product={productContext} extendedStyles={styles.productFitType} />
                </GridItem>
                {showTitle && (
                    <GridItem {...styles.descriptionGridItem}>
                        <StyledWrapper onClick={handleAnalyticsClick}>
                            {showPartNumbers && (
                                <ProductPartNumbers
                                    productNumber={product.productNumber}
                                    canadianTireNumber={canadianTireNumber}
                                    extendedStyles={styles.partNumbersStyles}
                                    productIndustryNumber={productIndustryNumber}
                                />
                            )}
                            <ProductDescription product={productContext} extendedStyles={styles.descriptionStyles} />
                        </StyledWrapper>
                        {showMiniPhoto && (
                            <StyledWrapper {...styles.miniPhotoWrapper}>
                                <ProductMiniPhoto
                                    product={product}
                                    extendedStyles={styles.miniPhoto}
                                    pdlDisplay={true}
                                ></ProductMiniPhoto>
                            </StyledWrapper>
                        )}
                    </GridItem>
                )}
                {showEta && product.properties.eta && (
                    <StyledWrapper>
                        <Typography {...styles.etaTitle}>{translate("ETA :")}</Typography>
                        <Typography {...styles.etaText}>{product.properties.eta}</Typography>
                    </StyledWrapper>
                )}
                {configSections && (
                    <StyledWrapper {...styles.wrapperConfigs}>
                        {configSections.map(
                            (section: ConfigSectionModel) =>
                                !!section.sectionOptions?.length && (
                                    <StyledWrapper {...styles.configSectionWrapper} key={section.id}>
                                        <Typography {...styles.configSectionLabel}>
                                            {translate("Additional Fees")}
                                        </Typography>
                                        {section.sectionOptions?.map(sectionOption => (
                                            <Typography {...styles.sectionOptionText} key={sectionOption.id}>
                                                {translate(section.sectionName)} (${sectionOption.price})
                                            </Typography>
                                        ))}
                                    </StyledWrapper>
                                ),
                        )}
                    </StyledWrapper>
                )}
            </GridContainer>
            <StyledWrapper {...styles.availabilityWrapper}>
                {settingsCollection.productSettings.showInventoryAvailability && showAvailability && (
                    <GridItem {...styles.availabilityGridItem}>
                        <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                    </GridItem>
                )}
            </StyledWrapper>
        </>
    );
    /* Store Custom Code - End */
};

export default connect(mapStateToProps)(withProductContext(ProductListProductInformation));
